import { useState } from 'react'
import { ArrowLeft, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  MAX_SITE_PAGES,
  type SiteImport,
  type SitePagesResult,
  useFindSitePages,
  useStartSiteImport,
} from '@/api/knowledgePages'
import { ImportProgress, SitePagesChecklist, samePageKey, usePageSelection } from './SitePages'
import { SiteAddressField } from './SiteAddressField'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  companyId: number | undefined
  agentId: number | undefined
  /** Source URLs of the pages the agent already has. */
  existingUrls: string[]
  /** The running or finished import, if any: the dialog then shows its progress. */
  importId: string | null
  importState: SiteImport | undefined
  onStarted: (importId: string) => void
  /** The finished import is dismissed. */
  onDone: () => void
}

// The dialog is a grid: without a bounded column, a long title widens it past the window.
const CONTENT = 'sm:max-w-lg grid-cols-[minmax(0,1fr)]'
const PRIMARY = 'bg-blue-600 hover:bg-blue-700 text-white'

/**
 * Importing a whole site into the knowledge of an existing agent: the address,
 * then the useful pages (5 at most), then the work of the server, page by page.
 */
export function SiteImportDialog({
  open,
  onOpenChange,
  companyId,
  agentId,
  existingUrls,
  importId,
  importState,
  onStarted,
  onDone,
}: Props) {
  const { t } = useTranslation()
  const ts = (key: string, opts?: Record<string, unknown>) => t(`manager.aiAgents.siteImport.${key}`, opts ?? {})
  const findPages = useFindSitePages(companyId, agentId)
  const startImport = useStartSiteImport(companyId, agentId)

  const [address, setAddress] = useState('')
  const [found, setFound] = useState<SitePagesResult | null>(null)
  const [error, setError] = useState('')

  const existing = new Set(existingUrls.filter(Boolean).map(samePageKey))
  const isExisting = (url: string) => existing.has(samePageKey(url))
  const selection = usePageSelection(found, isExisting)

  const search = async () => {
    if (!address.trim()) return
    setError('')
    try {
      setFound(await findPages.mutateAsync(address.trim()))
      selection.reset()
    } catch (e) {
      setError(searchError(e, ts))
    }
  }

  const reset = () => {
    setFound(null)
    selection.reset()
    setError('')
  }

  const launch = async () => {
    if (!found) return
    setError('')
    const pages = found.pages
      .filter((p) => selection.selected.includes(p.url))
      .map((p) => ({ url: p.url, titre: p.titre }))
    try {
      onStarted(await startImport.mutateAsync(pages))
      reset()
      setAddress('')
    } catch {
      setError(ts('errors.start'))
    }
  }

  const close = () => onOpenChange(false)

  // ─── Progress, page by page ────────────────────────────────────────────────
  if (importId) {
    const pages = importState?.pages ?? []
    const saved = pages.filter((p) => p.etat === 'enregistree').length
    const over = importState?.statut === 'termine'
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className={CONTENT}>
          <DialogHeader>
            <DialogTitle>{over ? ts('doneTitle') : ts('progressTitle')}</DialogTitle>
            <DialogDescription className="sr-only">{ts('progressTitle')}</DialogDescription>
          </DialogHeader>
          <ImportProgress pages={pages} />
          {importState?.statut === 'mise_a_jour' ? (
            <p className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
              <Loader2 className="h-4 w-4 animate-spin" />
              {ts('updatingAgent')}
            </p>
          ) : over ? (
            <p className="text-sm text-slate-700 dark:text-slate-200">
              {saved ? ts('agentUpdated', { count: saved }) : ts('nothingAdded')}
            </p>
          ) : null}
          <DialogFooter>
            {over ? (
              <Button
                type="button"
                onClick={() => {
                  onDone()
                  close()
                }}
                className={PRIMARY}
              >
                {ts('close')}
              </Button>
            ) : (
              <Button type="button" variant="outline" onClick={close}>
                {ts('background')}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  // ─── Choosing the pages ────────────────────────────────────────────────────
  if (found) {
    return (
      <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(o) : (reset(), close()))}>
        <DialogContent className={CONTENT}>
          <DialogHeader>
            <DialogTitle>{ts('chooseTitle')}</DialogTitle>
            <DialogDescription>{ts('chooseSubtitle', { max: MAX_SITE_PAGES })}</DialogDescription>
          </DialogHeader>
          <SitePagesChecklist
            pages={found.pages}
            selected={selection.selected}
            isExisting={isExisting}
            onToggle={selection.toggle}
          />
          {error ? <p className="text-sm text-red-600 dark:text-red-400">{error}</p> : null}
          <DialogFooter className="gap-2 sm:justify-between">
            <Button type="button" variant="ghost" onClick={reset} className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              {ts('back')}
            </Button>
            <Button
              type="button"
              onClick={launch}
              disabled={!selection.selected.length || startImport.isPending}
              className={`gap-2 ${PRIMARY}`}
            >
              {startImport.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {ts('importPages', { count: selection.selected.length })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  // ─── The address ───────────────────────────────────────────────────────────
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={CONTENT}>
        <DialogHeader>
          <DialogTitle>{ts('title')}</DialogTitle>
          <DialogDescription>{ts('subtitle', { max: MAX_SITE_PAGES })}</DialogDescription>
        </DialogHeader>
        <SiteAddressField
          id="site-import-url"
          value={address}
          onChange={setAddress}
          onSubmit={search}
          searching={findPages.isPending}
          error={error}
        />
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={close} disabled={findPages.isPending}>
            {ts('cancel')}
          </Button>
          <Button
            type="button"
            onClick={search}
            disabled={findPages.isPending || !address.trim()}
            className={`gap-2 ${PRIMARY}`}
          >
            {findPages.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {ts('find')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** The reason a site could not be read, in the user's words. */
export function searchError(e: unknown, ts: (key: string) => string) {
  const code = (e as { response?: { data?: { error?: string } } })?.response?.data?.error
  return code === 'adresse_refusee' || code === 'site_injoignable' || code === 'site_protege'
    ? ts(`errors.${code}`)
    : ts('errors.search')
}
